import { DisplayEntityIdType } from '@ydistri/api-sdk';

/**
 * Format store label based on the requested display type.
 * Label is usually a combination of id and name or code and name.
 * In some cases, the id already part of the name so it should
 * be displayed as is.
 * @param displayType Type used to display store id
 * @param storeName
 * @param storeCustomerId
 * @param storeCode
 */
export const formatStoreLabel = (
  displayType: DisplayEntityIdType,
  storeName: string,
  storeCustomerId?: string | null,
  storeCode?: string | null,
): string => {
  switch (displayType) {
    case DisplayEntityIdType.CustomerId:
      if (storeCustomerId) {
        return formatLabel(storeCustomerId, storeName);
      } else {
        return storeName;
      }
    case DisplayEntityIdType.Code:
      if (storeCode) {
        return formatLabel(storeCode, storeName);
      } else {
        return storeName;
      }
    case DisplayEntityIdType.None:
      return storeName;
  }
};

interface DisplayedStore {
  name: string;
  code?: string | null;
  customerStoreId?: string | null;
}

const displayedStoreId = (displayType: DisplayEntityIdType, store: DisplayedStore): string => {
  switch (displayType) {
    case DisplayEntityIdType.CustomerId:
      return store.customerStoreId ?? '';
    case DisplayEntityIdType.Code:
      return store.code ?? '';
    case DisplayEntityIdType.None:
      return store.name;
  }
};

/**
 * Compare stores by the identifier their label starts with (see formatStoreLabel), then by name.
 * Numeric-aware, so customer id 9 sorts before 10; a store without the identifier sorts first.
 * @param displayType Type used to display store id
 */
export const compareStoresByDisplayId =
  (displayType: DisplayEntityIdType) =>
  (left: DisplayedStore, right: DisplayedStore): number =>
    displayedStoreId(displayType, left).localeCompare(
      displayedStoreId(displayType, right),
      undefined,
      { numeric: true },
    ) || left.name.localeCompare(right.name, undefined, { numeric: true });

/**
 * Formatter function to format the label in unified format.
 * @param name
 * @param id
 */
const formatLabel = (id: string, name: string) => {
  return `${id} - ${name}`;
};
